import { useContext } from "react"
import { createViewContext } from "../../Components/createViewContext"
import { ApiClient, CruiseApi, DayApi, IAuditLogEdit, ICruise, ICruiseResponse, IDay, IDaysResponse, ILog, ILogsResponse, IShip, IStatus, ITrack, ITracksResponse, ITrip, ITripsResponse, LogApi, ShipApi, TrackApi, TripApi } from "../../Types"

export enum PrintMode {
  none,
  cruise,
  trips,
  days,
  ship,
}

// Discriminator for the print body: which sub-view (or "failed to load" message)
// the current print mode + loaded data resolves to. Extracted so the selection
// guards can be unit-tested without rendering the heavy print sub-tree.
export enum PrintContentKind {
  none = "none",
  cruise = "cruise",
  cruiseFailed = "cruiseFailed",
  trips = "trips",
  tripsFailed = "tripsFailed",
  days = "days",
  daysFailed = "daysFailed",
  ship = "ship",
}

// Print acquisition intentionally owns one immutable request snapshot. Generated
// entity-shaped rows here must not be normalized into the live DataStore.
export const PrintDataOwnership = "immutable-request-snapshot" as const;

export function printableDays(days: readonly IDay[]): IDay[] {
  // Zero is the undated next-day planning record, not a calendar date.
  return days.filter((day) => day.day !== 0);
}

export const PrintViewRequests = {
  loadCruise(api: ApiClient, shipId: number, cruiseId: number): Promise<ICruiseResponse> {
    return new CruiseApi(api).cruises.get({ params: { shipId, cruiseId } }).call();
  },
  loadTrips(api: ApiClient, shipId: number, cruiseId: number): Promise<ITripsResponse> {
    return new TripApi(api).trips.list({ params: { shipId, cruiseId } }).call();
  },
  loadCruiseTrack(api: ApiClient, shipId: number, cruiseId: number): Promise<ITracksResponse> {
    return new TrackApi(api).tracks.cruise({ params: { shipId, cruiseId } }).call();
  },
  loadTripDays(api: ApiClient, shipId: number, tripId: number): Promise<IDaysResponse> {
    return new DayApi(api).days.list({ params: { shipId, tripId } }).call();
  },
  loadTripTrack(api: ApiClient, shipId: number, tripId: number): Promise<ITracksResponse> {
    return new TrackApi(api).tracks.trip({ params: { shipId, tripId } }).call();
  },
  loadDayLogs(api: ApiClient, shipId: number, dayId: number): Promise<ILogsResponse> {
    return new LogApi(api).logs.list({ params: { shipId, dayId } }).call();
  },
  loadShipAttachments(api: ApiClient, shipId: number) {
    return new ShipApi(api).ships.attachments({ params: { shipId } }).call();
  },
  contentKind(printMode: PrintMode, data?: PrintViewData): PrintContentKind {
    if (!data) {
      return PrintContentKind.none;
    }
    switch (printMode) {
      case PrintMode.cruise:
        return data.cruise ? PrintContentKind.cruise : PrintContentKind.cruiseFailed;
      case PrintMode.trips:
        return data.trips && data.trips.length > 0 ? PrintContentKind.trips : PrintContentKind.tripsFailed;
      case PrintMode.days:
        return data.days && printableDays(data.days).length > 0 ? PrintContentKind.days : PrintContentKind.daysFailed;
      case PrintMode.ship:
        return PrintContentKind.ship;
      default:
        return PrintContentKind.none;
    }
  },
};

export type PrintRoot = ICruise;

export interface PrintViewData {
  cruise?: ICruise;
  cruises: ICruise[];
  ship?: IShip;
  tracks: ITrack[];
  trips: ITrip[];
  tripLists: ITrip[];
  days: IDay[];
  dayLists: IDay[];
  logs: ILog[];
  logsLists: ILog[];
  statuses?: IStatus[];
  // Every printed day's edit history, needed to render what a day-edit log entry
  // announces (issue #1285).
  auditLogEdits?: IAuditLogEdit[];
}

export enum TripListType {
  short = 1,
  details = 2,
}

export enum DayListType {
  short = 1,
  details = 2,
}

export interface PrintViewContextData {
  data?: PrintViewData;
  includeTripData: boolean;
  setIncludeTripData: (include: boolean) => void;
  includeTripDayListType: TripListType;
  setIncludeTripDayListType: (type: TripListType) => void;
  includeJournal: boolean;
  setIncludeJournal: (include: boolean) => void;
  includeTechnical: boolean;
  setIncludeTechnical: (include: boolean) => void;
  includeDayTrack: boolean;
  setIncludeDayTrack: (include: boolean) => void;
  includeDayForecast: boolean;
  setIncludeDayForecast: (include: boolean) => void;
  includeDayJournal: boolean;
  setIncludeDayJournal: (include: boolean) => void;
  includeDayTechnical: boolean;
  setIncludeDayTechnical: (include: boolean) => void;
  includeDayWatchplan: boolean;
  setIncludeDayWatchplan: (include: boolean) => void;
  includeLogs: boolean;
  setIncludeLogs: (include: boolean) => void;
  includeEdits: boolean;
  setIncludeEdits: (include: boolean) => void;

  includeDayLogListType: DayListType;
  setIncludeDayLogListType: (type: DayListType) => void;

}

const printViewContext = createViewContext<PrintViewContextData>();
export const PrintViewContext = printViewContext.Context;
export const GetPrintViewContext = printViewContext.useGet;

export const GetData = () => {
  const context = useContext(PrintViewContext);
  return context ? context.data : undefined;
};
